import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Sheet,
  Stack,
  Table,
  Typography,
} from "@mui/joy";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { Navigate } from "react-router-dom";
import Page from "../components/Pages/Page";
import PageHeader from "../components/PageHeader";
import Loading from "../components/Loading";
import PageError from "../components/PageError";
import { User } from "../common/types";
import { useReadUser } from "../services/api";
import {
  OrgHeartbeatOverviewItem,
  useOrgHeartbeatsOverview,
} from "../services/orgHeartbeatsOverview";

interface OrgHeartbeatsOverviewViewProps {
  items: OrgHeartbeatOverviewItem[];
  loading?: boolean;
  error?: string | null;
}

const statusColor = (status: string) => {
  switch (status) {
    case "pending":
      return "warning";
    case "running":
      return "primary";
    case "completed":
      return "success";
    case "cancelled":
    case "expired":
      return "neutral";
    case "failed":
      return "danger";
    default:
      return "neutral";
  }
};

const heartbeatKey = (item: OrgHeartbeatOverviewItem, index: number) =>
  [
    item.user_name,
    item.workspace_name,
    item.title || "Untitled",
    item.frequency,
    item.kind,
    item.status,
    index,
  ].join(":");

const HeartbeatCard: React.FC<{ item: OrgHeartbeatOverviewItem }> = ({
  item,
}) => (
  <Card variant="outlined" sx={{ borderRadius: "8px" }}>
    <CardContent>
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1}
        >
          <Typography level="title-sm" sx={{ wordBreak: "break-word" }}>
            {item.user_name}
          </Typography>
          <Chip
            size="sm"
            variant="soft"
            color={statusColor(item.status) as any}
          >
            {item.status}
          </Chip>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography level="body-xs" color="neutral" sx={{ fontWeight: 500 }}>
            Workspace:
          </Typography>
          <Typography level="body-sm" sx={{ wordBreak: "break-word" }}>
            {item.workspace_name}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography level="body-xs" color="neutral" sx={{ fontWeight: 500 }}>
            Title:
          </Typography>
          <Typography level="body-sm" sx={{ wordBreak: "break-word" }}>
            {item.title || "Untitled"}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          <Typography
            component="code"
            level="body-sm"
            sx={{ fontFamily: "monospace" }}
          >
            {item.frequency}
          </Typography>
          <Chip size="sm" variant="soft" color="neutral">
            {item.kind}
          </Chip>
        </Stack>
      </Stack>
    </CardContent>
  </Card>
);

export const OrgHeartbeatsOverviewView: React.FC<
  OrgHeartbeatsOverviewViewProps
> = ({ items, loading = false, error = null }) => {
  const visibleItems = useMemo(
    () =>
      items
        .filter(
          (item) => item.status !== "failed" && item.status !== "cancelled",
        )
        .sort((left, right) => {
          return (
            left.user_name.localeCompare(right.user_name) ||
            left.workspace_name.localeCompare(right.workspace_name) ||
            (left.title || "Untitled").localeCompare(right.title || "Untitled")
          );
        }),
    [items],
  );

  return (
    <Page>
      <PageHeader>
        <Box>
          <Typography level="h1">Heartbeat Overview</Typography>
          <Typography level="body-sm" color="neutral">
            Read-only organization view of active and historical heartbeat
            registrations.
          </Typography>
        </Box>
      </PageHeader>

      <Stack spacing={2}>
        <Alert color="neutral" variant="soft">
          Labels are added by users — older tasks may be unlabeled.
        </Alert>

        {error && (
          <Alert color="danger" variant="soft">
            {error}
          </Alert>
        )}

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FavoriteBorderIcon color="primary" />
                <Typography level="h3">Organization Heartbeats</Typography>
              </Stack>

              {loading ? (
                <Loading />
              ) : visibleItems.length === 0 ? (
                <Alert color="neutral" variant="soft">
                  No heartbeat tasks are currently visible for this
                  organization.
                </Alert>
              ) : (
                <>
                  {/* Desktop table view */}
                  <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <Sheet variant="outlined" sx={{ borderRadius: "8px" }}>
                      <Table hoverRow>
                        <thead>
                          <tr>
                            <th style={{ width: "22%" }}>User</th>
                            <th>Workspace</th>
                            <th>Title</th>
                            <th>Frequency</th>
                            <th>Kind</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleItems.map((item, index) => (
                            <tr key={heartbeatKey(item, index)}>
                              <td>{item.user_name}</td>
                              <td>{item.workspace_name}</td>
                              <td>{item.title || "Untitled"}</td>
                              <td>
                                <Typography
                                  component="code"
                                  level="body-sm"
                                  sx={{ fontFamily: "monospace" }}
                                >
                                  {item.frequency}
                                </Typography>
                              </td>
                              <td>
                                <Chip size="sm" variant="soft" color="neutral">
                                  {item.kind}
                                </Chip>
                              </td>
                              <td>
                                <Chip
                                  size="sm"
                                  variant="soft"
                                  color={statusColor(item.status) as any}
                                >
                                  {item.status}
                                </Chip>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Sheet>
                  </Box>

                  {/* Mobile card view */}
                  <Box sx={{ display: { xs: "block", md: "none" } }}>
                    <Stack spacing={2}>
                      {visibleItems.map((item, index) => (
                        <HeartbeatCard
                          key={heartbeatKey(item, index)}
                          item={item}
                        />
                      ))}
                    </Stack>
                  </Box>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Page>
  );
};

const OrgHeartbeatsOverview: React.FC = () => {
  const readUserAPI = useReadUser();
  const readOverview = useOrgHeartbeatsOverview();
  const [user, setUser] = useState<User | null>(null);
  const [items, setItems] = useState<OrgHeartbeatOverviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const userData = await readUserAPI();
        setUser(userData);

        if (userData.role_in_org !== "admin") {
          setLoading(false);
          return;
        }

        if (!userData.organization_id) {
          throw new Error("Organization context is required.");
        }

        const response = await readOverview(userData.organization_id);
        setItems(response.items || []);
      } catch (err: any) {
        setError(err?.message || "Failed to load heartbeat overview.");
      } finally {
        setLoading(false);
      }
    };

    load();
    // Match other org-admin views that fetch user once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!loading && user?.role_in_org !== "admin") {
    return <Navigate to="/" replace />;
  }

  if (!loading && !user && error) {
    return <PageError>{error}</PageError>;
  }

  return (
    <OrgHeartbeatsOverviewView items={items} loading={loading} error={error} />
  );
};

export default OrgHeartbeatsOverview;
